import Icon from '@mdi/react';
import React from 'react';
import { Variables } from '../base';
import { IJourneyStageView } from './claim-progress-derivation';
import { IStageVisual, STAGE_VISUAL, TONE_COLORS } from './claim-progress-visuals';
import * as S from './claim-stage-tracker.styles';

interface IClaimStageTrackerProps {
    stages: IJourneyStageView[];
}

const colorsFor = (tone: IStageVisual['tone'], state: string) => {
    if (tone === 'pending') {
        return {
            background: Variables.ThemeLight.Background2,
            border: Variables.ThemeLight.Border1,
            foreground: state === 'PENDING' ? Variables.ThemeLight.TextMild : Variables.ThemeLight.TextXMild,
        };
    }
    const colors = TONE_COLORS[tone];
    return {
        background: colors.background,
        // The active node gets the stronger primary outline; every other tone keeps its own.
        border: state === 'ACTIVE' ? Variables.ThemeLight.PrimaryConstant6 : colors.border,
        foreground: colors.foreground,
    };
};

/**
 * Five-node claim journey. A node's state is its own (it is never derived from "every node before
 * the current index is green"), so a skipped coverage, a closure in progress and a completed stage
 * with rework active can all be shown.
 */
export const ClaimStageTracker: React.FC<IClaimStageTrackerProps> = ({ stages }) => (
    <S.JourneyContainer aria-label="Claim journey">
        <S.SectionLabel>Claim Journey</S.SectionLabel>
        <S.ChevronRow>
            {stages.map((stage, index) => {
                const visual = STAGE_VISUAL[stage.state];
                const colors = colorsFor(visual.tone, stage.state);
                const first = index === 0;
                const last = index === stages.length - 1;
                const muted =
                    stage.state === 'PENDING' || stage.state === 'NOT_APPLICABLE' || stage.state === 'UNKNOWN';

                return (
                    <S.ChevronCell
                        key={stage.stage}
                        $first={first}
                        aria-current={stage.state === 'ACTIVE' || stage.state === 'BLOCKED' ? 'step' : undefined}
                    >
                        <S.Chevron $first={first} $last={last} $background={colors.background} $border={colors.border}>
                            <S.ChevronIcon $color={colors.foreground}>
                                <Icon path={visual.icon} size={0.9} />
                            </S.ChevronIcon>
                            <S.ChevronText>
                                <S.ChevronLabel $muted={muted}>
                                    {index + 1}. {stage.label}
                                </S.ChevronLabel>
                                <S.ChevronCaption $color={colors.foreground}>
                                    {stage.caption || visual.caption}
                                </S.ChevronCaption>
                            </S.ChevronText>
                        </S.Chevron>
                    </S.ChevronCell>
                );
            })}
        </S.ChevronRow>
    </S.JourneyContainer>
);
